'use client';

import { useState, useEffect } from 'react';
import { Menu, X, Mountain } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Navigation = (): JSX.Element => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; size: number; opacity: number; speed: number; color: string }>>([]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize floating particles
  useEffect(() => {
    const colors = ['#7C8F75', '#A8B89A', '#ffffff', '#E8E3DD'];
    const initialParticles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      speed: Math.random() * 0.5 + 0.2,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
    setParticles(initialParticles);

    // Animate particles
    const animateParticles = () => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        x: particle.x + Math.sin(Date.now() * 0.001 + particle.id) * 0.3,
        y: particle.y <= -5 ? 105 : particle.y - particle.speed,
        opacity: Math.sin(Date.now() * 0.002 + particle.id) * 0.3 + 0.4,
        size: particle.size + Math.sin(Date.now() * 0.003 + particle.id) * 0.3
      })));
    };

    const interval = setInterval(animateParticles, 50);
    return () => clearInterval(interval);
  }, []);

  const menuItems: Array<{ label: string; href: string }> = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Amenities', href: '#amenities' },
    { label: 'Book Now', href: '#booking' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-in-out ${
          isScrolled 
            ? 'bg-gradient-to-r from-white/95 via-[#F9F6F2]/95 to-white/95 backdrop-blur-xl shadow-2xl border-b border-gradient-to-r from-[#7C8F75]/20 via-[#A8B89A]/20 to-[#7C8F75]/20' 
            : 'bg-gradient-to-r from-black/20 via-black/10 to-black/20 backdrop-blur-md'
        }`}
        style={{
          background: isScrolled 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(249,246,242,0.95) 50%, rgba(255,255,255,0.95) 100%)'
            : 'linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(124,143,117,0.1) 25%, rgba(168,184,154,0.1) 75%, rgba(0,0,0,0.2) 100%)'
        }}
      >
        {/* Floating Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full animate-pulse"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                backgroundColor: particle.color,
                opacity: particle.opacity * (isScrolled ? 0.3 : 0.8),
                transform: `translateZ(0) scale(${1 + Math.sin(Date.now() * 0.001 + particle.id) * 0.2})`,
                boxShadow: `0 0 ${particle.size * 3}px ${particle.color}`,
                transition: 'opacity 0.7s ease-in-out'
              }}
            />
          ))}
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#7C8F75]/5 to-transparent animate-pulse"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo with Enhanced Effects */}
            <div 
              className="flex items-center space-x-3 cursor-pointer group transition-all duration-500 hover:scale-110" 
              onClick={() => scrollToSection('#home')}
            >
              <div className="relative">
                {/* Glowing background */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] rounded-full scale-0 group-hover:scale-150 opacity-0 group-hover:opacity-30 transition-all duration-500 blur-xl"></div>
                
                {/* Icon with gradient */}
                <div className="relative bg-gradient-to-br from-[#7C8F75] to-[#A8B89A] p-2 rounded-xl shadow-lg group-hover:shadow-2xl transition-all duration-500">
                  <Mountain className={`h-6 w-6 text-white transition-all duration-500 group-hover:scale-110`} />
                </div>
              </div>
              
              <div className="relative">
                <span className={`text-xl font-bold transition-all duration-500 bg-gradient-to-r ${
                  isScrolled 
                    ? 'from-[#333333] via-[#7C8F75] to-[#333333] bg-clip-text text-transparent' 
                    : 'from-white via-[#F9F6F2] to-white bg-clip-text text-transparent drop-shadow-lg'
                } group-hover:from-[#7C8F75] group-hover:via-[#A8B89A] group-hover:to-[#7C8F75]`}>
                  Mountain View
                </span>
                
                {/* Animated underline */}
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] group-hover:w-full transition-all duration-500"></div>
              </div>
            </div>

            {/* Desktop Navigation with Enhanced Styling */}
            <div className="hidden lg:flex items-center space-x-2">
              {menuItems.map((item, index) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className={`relative px-6 py-3 rounded-2xl font-medium transition-all duration-500 group overflow-hidden ${
                    isScrolled
                      ? 'text-[#333333] hover:text-white'
                      : 'text-white/90 hover:text-white'
                  }`}
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  {/* Gradient background on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75] via-[#A8B89A] to-[#7C8F75] opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-x-0 group-hover:scale-x-100"></div>
                  
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  
                  {/* Text */}
                  <span className="relative z-10">{item.label}</span>
                  
                  {/* Floating dots */}
                  <div className="absolute top-1 right-1 w-1 h-1 bg-current rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-0 group-hover:scale-100"></div>
                  <div className="absolute bottom-1 left-1 w-1 h-1 bg-current rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 transform scale-0 group-hover:scale-100"></div>
                </button>
              ))}
            </div>

            {/* Enhanced Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className={`lg:hidden relative overflow-hidden transition-all duration-500 hover:scale-110 ${
                isScrolled 
                  ? 'text-[#333333] hover:text-white' 
                  : 'text-white hover:text-white'
              }`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {/* Gradient background */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] opacity-0 hover:opacity-100 transition-all duration-500 rounded-lg"></div>
              
              {/* Icon with rotation animation */}
              <div className="relative z-10 transition-transform duration-500">
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6 rotate-180" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </div>
            </Button>
          </div>
        </div>

        {/* Bottom gradient line */}
        <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#7C8F75]/50 to-transparent transition-opacity duration-700 ${
          isScrolled ? 'opacity-100' : 'opacity-0'
        }`}></div>
      </nav>

      {/* Enhanced Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-700 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Enhanced Backdrop with particles */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-black/70 via-[#7C8F75]/20 to-black/70 backdrop-blur-lg" 
          onClick={() => setIsMobileMenuOpen(false)} 
        >
          {/* Mobile particles */}
          <div className="absolute inset-0 overflow-hidden">
            {particles.slice(0, 8).map((particle) => (
              <div
                key={`mobile-${particle.id}`}
                className="absolute rounded-full animate-pulse"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size * 2}px`,
                  height: `${particle.size * 2}px`,
                  backgroundColor: particle.color,
                  opacity: particle.opacity * 0.4,
                  boxShadow: `0 0 ${particle.size * 4}px ${particle.color}`,
                }}
              />
            ))}
          </div>
        </div>
        
        {/* Enhanced Mobile Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] transform transition-all duration-700 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,246,242,0.98) 50%, rgba(255,255,255,0.98) 100%)',
            backdropFilter: 'blur(20px)',
            boxShadow: '-20px 0 40px rgba(0,0,0,0.3)'
          }}
        >
          {/* Enhanced Mobile Menu Header */}
          <div className="relative overflow-hidden">
            <div className="bg-gradient-to-r from-[#7C8F75] via-[#A8B89A] to-[#7C8F75] p-6 text-white relative">
              {/* Header particles */}
              <div className="absolute inset-0 overflow-hidden">
                {particles.slice(0, 5).map((particle) => (
                  <div
                    key={`header-${particle.id}`}
                    className="absolute rounded-full animate-pulse"
                    style={{
                      left: `${particle.x}%`,
                      top: `${particle.y}%`,
                      width: `${particle.size}px`,
                      height: `${particle.size}px`,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      opacity: particle.opacity * 0.6,
                    }}
                  />
                ))}
              </div>
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
                    <Mountain className="h-6 w-6" />
                  </div>
                  <span className="text-xl font-bold">Mountain View</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 transition-all duration-300 hover:scale-110"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>
          </div>

          {/* Enhanced Mobile Menu Items */}
          <div className="p-6 space-y-3">
            {menuItems.map((item, index) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="w-full text-left p-4 rounded-2xl font-medium transition-all duration-500 transform hover:scale-105 text-[#333333] hover:text-white relative overflow-hidden group"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Gradient background */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-x-0 group-hover:scale-x-100"></div>
                
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                
                {/* Content */}
                <div className="relative z-10 flex items-center space-x-3">
                  <div className="w-2 h-2 bg-current rounded-full opacity-50 group-hover:opacity-100 transition-all duration-300"></div>
                  <span>{item.label}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Enhanced Mobile Menu Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#F9F6F2] to-transparent border-t border-[#7C8F75]/10">
            <div className="text-center space-y-4">
              <p className="text-sm text-[#333333]/70">Experience luxury at Mountain View</p>
              <button
                onClick={() => scrollToSection('#booking')}
                className="w-full relative overflow-hidden bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] text-white py-4 px-6 rounded-2xl font-semibold transition-all duration-500 transform hover:scale-105 shadow-xl hover:shadow-2xl group"
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                
                <span className="relative z-10">Book Your Stay</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;